import { ol_geom } from "@omnia/exports/openlayers"
import { DatalayerZone, IGenericAnalysis, ISoilAnalysis, ISoilAnalysisAlloc, isUndefined, models, SoilAnalysisType, ZoneTextLocation } from "../omnia"
import { getSsmAttributesAsMap } from "../ssm/attrs"
import { SsmAttribute } from "../ssm/types"
import { IDataLayerValue, LayerTypeId, NitrogenAnalysisType } from "../types"
import { SoilAnalysisLayer } from "./soilAnalysisLayer"

export class SoilAnalysisZone extends DatalayerZone {

    private _location: ol_geom.Geometry;

    getAnalysis(): IGenericAnalysis {
        return this._value?.Data as IGenericAnalysis
    }

    getSoilAnalysis(): ISoilAnalysis {
        return this._value?.Data as ISoilAnalysis
    }

    isAllocated() {
        return Boolean(this._value?.Data)
    }

    isSsmAnalysis() {
        const a = this.getSoilAnalysis()
        return a?.Type == SoilAnalysisType.SSM
    }

    isMorganAnalysis() {
        const a = this.getSoilAnalysis()
        return a?.P2O5Method == models.SoilP2O5Method.Morgan
    }

    protected getValueDescription(): string {
        return this.getSoilAnalysisDescription();
    }

    getSoilAnalysisDescription(a?: ISoilAnalysis) {
        a = a || this.getSoilAnalysis();
        return getAnalysisZoneDescription(a)
    }

    getCurrentViewerValue(): number {
        var v = this._layer.viewer;
        if (v) return v.getZoneValue(this);
    }

    getCurrentViewerValueText(): string {
        return this.getZoneText(ZoneTextLocation.MapTooltip);
    }

    getAllocatableValues(): IDataLayerValue[] {

        var values: IDataLayerValue[];

        var layer = <SoilAnalysisLayer>(this.layer);

        if (!layer.showAllAnalysis) {
            values = this.getSupportedValuesForField(false);
        }
        else {
            values = this.layer.type.Values.filter(v => (v != this._value));
        }

        values = values.sort((v1, v2) => v1.Name.localeCompare(v2.Name));

        return values;

    }

    getSupportedValuesForField(includeCurrent: boolean): IDataLayerValue[] {

        var values = this.layer.supportedValues;

        values = values.filter(v => this.checkFieldAssoc(v));

        if (includeCurrent) {

            var current = this.value;

            if (current && (!values.find(v => v == current))) {
                values = [current].concat(values);
            }

        }

        return values;
    }

    private checkFieldAssoc(item: { Data: IGenericAnalysis }) {

        let analysis = item.Data;

        if (analysis && analysis.Allocations) {

            let fieldId = this.layer.fieldId;
            let checkZoneAlloc = (this.layer.typeId == LayerTypeId.SoilAnalysis);

            return analysis.Allocations.some(alloc => {

                if (alloc.FieldId != fieldId) return false;

                if (checkZoneAlloc) {
                    if ((<ISoilAnalysis>analysis).ZoneAnalysis) return true;
                    if ((<ISoilAnalysisAlloc>alloc).ZoneAlloc) return true;
                    return false;
                }

                return true;

            });
        }

    }


    getText() {

        let texts = super.getText();

        let analysis = this.getAnalysis();

        if (analysis) {
            let type = <NitrogenAnalysisType>analysis["AnalysisType"];

            if (type == NitrogenAnalysisType.FarmAssessment) {

                texts.forEach(t => {
                    t.italic = true;
                });

            }
        }

        return texts;

    }


    getLocationGeom() {

        if (isUndefined(this._location)) {

            let geom: ol_geom.Geometry = null;

            let analysis = this.getAnalysis();

            if (analysis) {

                let locationWkt = analysis.Location;

                if (locationWkt) {
                    geom = this.layer.getGeometryFromWKT(locationWkt);
                }

            }

            this._location = geom;

        }

        return this._location;

    }

    getDefaultZoneName() {

        if (this.zone.Name) {
            return this.zone.Name;
        }

        if (this.zone.Number) {
            return "Zone " + this.zone.Number.toString();
        }

    }

    /**
     * Returns the definition of an SSM attribute for the specific property name
     * The list of available attributes is initialized on demand, and stored at context level
     */
    getSsmAttribute(property: string) {

        const key = "attributes"

        const context = this.layer.context

        const cache = context.getCache("soil.ssm")

        let attrs = cache[key] as Map<string, SsmAttribute>

        if (!attrs) {
            const cls = context.sharedData.soil.ssmClassification
            attrs = cache[key] = getSsmAttributesAsMap(cls)
        }

        return attrs.get(property)

    }

}

export function getAnalysisZoneDescription(a: ISoilAnalysis) {

    if (a) {

        let text = a.FieldReference;

        if (!text) {

            text = a.AnalysisNumber;

        }
        else {

            const plotRef = a.PlotReference;

            if (plotRef) {
                text += " - " + plotRef;
                text = `${text} - ${plotRef}`
            }

            if (a.AnalysisNumber) {
                text = `${text} (${a.AnalysisNumber})`
            }
        }

        if (a.FirstYear) {
            text = `${text} (${a.FirstYear})`
        }

        return text;

    }

}
